import React from 'react';
import './NotesBoard.css';

function NotesBoard({ 
  notes, 
  user, 
  onEdit,
  onView,
  onDelete, 
  onShare, 
  onExport,
  onTogglePin,
  onCopyLink,         // Build 26: per-card "Copy Link" handler
  onPinToDesktop,     // Build 26: per-card "Pin to desktop" handler
  onMoveToFolder,     // Build 27 (item 1): move note to one of my folders
  myFolders = [],     // Build 27: folders the current user can file into
  showExportDropdown,
  setShowExportDropdown
}) {
  const isOwner = (note) => note.createdBy === user.uid;
  const canEdit = (note) => {
    if (isOwner(note)) return true;
    return note.sharedWith?.some(s => s.userId === user.uid && s.permission === 'edit');
  };

  return (
    <div className="notes-board">
      {notes.length === 0 ? (
        <div className="empty-state">
          <h2>No notes found</h2>
          <p>Create your first note or adjust your filters</p>
        </div>
      ) : (
        <div className="notes-grid">
          {notes.map(note => (
            <div key={note.id} className={`note-card ${note.pinned ? 'pinned' : ''}`}>
              <div className="note-card-header">
                <button
                  className="btn-icon pin-btn"
                  onClick={() => onTogglePin(note)}
                  title={note.pinned ? 'Unpin from top' : 'Pin to top'}
                >
                  {note.pinned ? '📌' : '📍'}
                </button>
                <h3 className="note-title">{note.title}</h3>
                {!isOwner(note) && (
                  <span className="shared-badge">
                    Shared ({note.sharedWith?.find(s => s.userId === user.uid)?.permission || 'view'})
                  </span>
                )}
              </div>
              
              {note.category && (
                <span className="note-category-badge">{note.category}</span>
              )}
              
              <div 
                className="note-content-preview" 
                dangerouslySetInnerHTML={{ 
                  __html: note.content.substring(0, 150) + (note.content.length > 150 ? '...' : '')
                }} 
              />
              
              {note.tags && note.tags.length > 0 && (
                <div className="note-tags">
                  {note.tags.map((tag, idx) => (
                    <span key={idx} className="note-tag">#{tag}</span>
                  ))}
                </div>
              )}
              
              <div className="note-meta">
                <span className="note-author">By: {note.createdByEmail}</span>
                <span className="note-date">
                  Updated: {note.updatedAt?.toDate?.().toLocaleDateString() || 'N/A'}
                </span>
              </div>
              
              {note.sharedWith?.length > 0 && (
                <div className="note-shared-info">
                  Shared with {note.sharedWith.length} user{note.sharedWith.length !== 1 ? 's' : ''}
                </div>
              )}
              
              <div className="note-actions">
                <button 
                  className="btn btn-small btn-view"
                  onClick={() => onView(note)}
                >
                  👁️ View
                </button>
                {canEdit(note) && (
                  <button 
                    className="btn btn-small btn-secondary"
                    onClick={() => onEdit(note)}
                  >
                    Edit
                  </button>
                )}
                {/* Build 26: per-card Copy Link + Pin to Desktop. Available to
                    everyone with read access — note shortcuts and permalinks
                    don't require ownership. Handlers passed by NotesApp. */}
                {onCopyLink && (
                  <button
                    className="btn btn-small btn-secondary"
                    onClick={() => onCopyLink(note)}
                    title="Copy a shareable link to this note"
                  >
                    🔗 Copy Link
                  </button>
                )}
                {onPinToDesktop && (
                  <button
                    className="btn btn-small btn-secondary"
                    onClick={() => onPinToDesktop(note)}
                    title="Pin this note to your desktop"
                  >
                    📎 Pin
                  </button>
                )}
                {onMoveToFolder && (
                  <select
                    className="btn btn-small btn-secondary note-move-select"
                    value=""
                    onChange={(e) => {
                      if (e.target.value !== '') {
                        // '__root__' sentinel = move out to "My Notes"
                        const dest = e.target.value === '__root__' ? null : e.target.value;
                        onMoveToFolder(note, dest);
                        e.target.value = '';
                      }
                    }}
                    title="Move this note into one of your folders"
                  >
                    <option value="">📂 Move to…</option>
                    <option value="__root__">My Notes (no folder)</option>
                    {myFolders.map(f => (
                      <option key={f.id} value={f.id}>{f.name}</option>
                    ))}
                  </select>
                )}
                {isOwner(note) && (
                  <>
                    <button 
                      className="btn btn-small btn-secondary"
                      onClick={() => onShare(note)}
                    >
                      Share
                    </button>
                    <button 
                      className="btn btn-small btn-cancel"
                      onClick={() => onDelete(note)}
                    >
                      Delete
                    </button>
                  </>
                )}
                <div className="export-dropdown-inline">
                  <button 
                    className="btn btn-small btn-secondary"
                    onClick={() => setShowExportDropdown(showExportDropdown === note.id ? null : note.id)}
                  >
                    Export ▼
                  </button>
                  {showExportDropdown === note.id && (
                    <div className="dropdown-menu">
                      <button onClick={() => onExport(note, 'markdown')}>Markdown (.md)</button>
                      <button onClick={() => onExport(note, 'pdf')}>PDF (.pdf)</button>
                      <button onClick={() => onExport(note, 'text')}>Text (.txt)</button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default NotesBoard;
